import type { ICost as ICountryCost } from '@bridebook/models/source/models/Countries/Costs.types';
import type { IDatePicker } from '@bridebook/models/source/models/Weddings.types';
import type { ICost as IWeddingCost } from '@bridebook/models/source/models/Weddings/Costs.types';
import type { ISupplier } from '@bridebook/models/source/models/Weddings/Suppliers.types';
import gazetteer, { type CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { processCosts } from '@bridebook/web/lib/budget/Costs.utils';
import INIT_FORM from '@bridebook/web/lib/budget/budget-init-form';
import { deriveBudgetInitFields, prepareInitProps } from '@bridebook/web/lib/budget/derive-init';
import { getCostsTranslation } from '@bridebook/web/lib/countries/data/costs';
import type { BudgetLine, BudgetProjection } from './types';

// The optional categories the couple asked the coach to leave out. The wedding has no
// field for this (the budget page keeps it only as form state), so the coach stores it.
export type BudgetOverrides = {
  excludeCategories?: string[];
};

// A cost doc as the resolver reads it: the fields shared by the stored record and the
// serialised store cost, so either one works as is.
export type StoredCost = Pick<
  IWeddingCost,
  'id' | 'name' | 'estimate' | 'actual' | 'group' | 'percentage' | 'supplierType'
>;

export type ResolveBudgetInput = {
  // Country cost templates, fetched with targets.
  costsInitial: Record<string, ICountryCost> | undefined;
  // The couple's own cost docs.
  weddingCosts: Record<string, StoredCost> | undefined;
  // False while the budget is still loading.
  budgetLoaded: boolean;
  // Typed as the app's budget maths expects, since it is passed straight to it.
  shortlist: Record<string, ISupplier> | undefined;
  date: IDatePicker | undefined;
  country: string | null | undefined;
  guestEstimate: number | null | undefined;
  budget: number | null | undefined;
  isGroomOnly: boolean;
  isBrideOnly: boolean;
  overrides: BudgetOverrides | undefined;
};

/**
 * The couple's budget breakdown for the coach: their own when they have one, otherwise
 * projected from the country cost targets and their date, guest estimate and budget.
 * The projection lets the coach answer with the couple's own numbers. It is never saved.
 */
export const resolveBudget = (input: ResolveBudgetInput): BudgetProjection | null => {
  const { costsInitial, weddingCosts, budgetLoaded, budget } = input;
  if (!costsInitial || !budgetLoaded) {
    return null;
  }

  const existing = Object.values(weddingCosts ?? {});
  if (existing.length > 0) {
    return {
      projected: false,
      target: budget ?? null,
      allocation: existing
        .map((cost) => storedLine(cost, costsInitial))
        .sort((a, b) => b.estimate - a.estimate),
      assumptions: null,
    };
  }

  return projectBudget(input, costsInitial);
};

/**
 * The cost name the couple sees in the app. A template cost takes the template's name,
 * translated when possible. A custom cost uses its stored name.
 *
 * A missing translation returns the raw key, which the model would read as a real name,
 * so that case is detected by comparing with the key itself.
 */
const costName = (
  id: string,
  stored: string | undefined,
  costsInitial: Record<string, ICountryCost>,
): string => {
  const template = costsInitial[id];
  if (!template) {
    return stored || id;
  }
  const key = template.i18nKey;
  const translated = key ? getCostsTranslation[key]?.() : undefined;
  const resolved = translated && translated !== `countries:costs.${key}` ? translated : undefined;
  return resolved || template.name || stored || id;
};

const storedLine = (cost: StoredCost, costsInitial: Record<string, ICountryCost>): BudgetLine => {
  const template = costsInitial[cost.id];
  return {
    costId: cost.id,
    name: costName(cost.id, cost.name, costsInitial),
    group: cost.group ?? template?.group ?? null,
    supplierType: cost.supplierType ?? template?.supplierType ?? null,
    percentage: cost.percentage ?? 0,
    estimate: cost.estimate ?? 0,
    actual: cost.actual ?? 0,
  };
};

// The app refuses to set up a real budget below this.
const MIN_PROJECTABLE_BUDGET = 1000;

const DAY_LABELS: Record<string, string> = {
  sat: 'Saturday',
  'fri-sun': 'Friday or Sunday',
  midweek: 'midweek',
};

// Runs the app's own budget allocation over the country targets, so the projection
// always matches what the budget page produces.
const projectBudget = (
  input: ResolveBudgetInput,
  costsInitial: Record<string, ICountryCost>,
): BudgetProjection | null => {
  const { date, country, guestEstimate, budget, shortlist, isGroomOnly, isBrideOnly, overrides } =
    input;
  if (!country) {
    return null;
  }

  const market = gazetteer.getMarketByCountry(country as CountryCodes);
  const fields = deriveBudgetInitFields({ date, guestsEstimate: guestEstimate, budget }, market);

  // Start from the form the couple would see. Its defaults leave some optional
  // categories unticked, so the projection matches the budget they would get.
  const optionalCategories = { ...INIT_FORM.fields.optionalCategories };
  for (const category of overrides?.excludeCategories ?? []) {
    optionalCategories[category as keyof typeof optionalCategories] = false;
  }
  const init = prepareInitProps({ ...fields, optionalCategories }, false);
  // Everything left out, defaults included: the coach states the full list.
  const excluded = init.excludeCategories;
  // Which inputs the couple gave, so the coach hedges only on the guessed ones.
  const targetAssumed = !(budget && budget > 0);
  const guestsAssumed = !(guestEstimate && guestEstimate > 0);
  // Weekday or season is 'undecided' when the date cannot supply it, and the form then
  // defaults to a peak-season Saturday.
  const dateAssumed = fields.timeOfWeek === 'undecided' || fields.timeOfYear === 'undecided';

  // The app never sets up a budget below this, and the allocation throws when the
  // estimate is under the lowest target band.
  if (init.estimate < MIN_PROJECTABLE_BUDGET) {
    return null;
  }

  let costs;
  try {
    costs = processCosts({
      costsInitial,
      init,
      shortlist: shortlist ?? {},
      isGroomOnly,
      isBrideOnly,
    });
  } catch {
    // This runs during render, so a throw would crash the planner page.
    return null;
  }

  return {
    projected: true,
    target: init.estimate,
    allocation: costs
      .map(
        (cost): BudgetLine => ({
          costId: cost.id ?? '',
          // The allocation output has no `name`, so add it back here.
          name: costName(cost.id ?? '', undefined, costsInitial),
          group: cost.group ?? null,
          supplierType: cost.supplierType ?? null,
          percentage: cost.percentage ?? 0,
          estimate: cost.estimate ?? 0,
          actual: cost.actual ?? 0,
        }),
      )
      .sort((a, b) => b.estimate - a.estimate),
    assumptions: {
      // The couple's own number, not the band ceiling the maths uses. Both give the
      // same result, and repeating the ceiling to the couple would look like an error.
      guests: guestEstimate && guestEstimate > 0 ? guestEstimate : init.guests,
      day: DAY_LABELS[init.day],
      season: init.season === 'off-season' ? 'off-peak' : 'peak',
      excluded,
      targetAssumed,
      guestsAssumed,
      dateAssumed,
    },
  };
};
